import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { goBack, push } from 'connected-react-router';
import cx from 'classnames';

// Types
import { AppDispatch } from 'types/RootState.types';
import { MyJohnLewisProps } from './MyJohnLewis.types';

// Design System
import { Modal } from 'jl-design-system/components/modal/Modal';
import { SecondaryButton, UnderlinedButton } from 'jl-design-system/elements/button/Button';
import { destroyForm } from 'jl-design-system/redux/actions/form/formActions';
import { resetAddressSearch } from 'jl-design-system/redux/actions/address/addressActions';

// redux
import { MYJL_ADDRESS, ANALYTICS_MY_JL_JOIN_MODAL_OPEN } from '@constants/actionConstants';
import routeConstants from '@constants/routeConstants';
import userConstants from '@constants/userConstants';
import { openSessionEndedModal } from '@redux/actions/app/appActions';
import {
  onEnterManualAddress,
  onLoqateNetworkTimeoutCallback,
  onLoqateSearch,
  triggerLoqateTimeoutEnterAddressManuallyAnalytics,
} from '@redux/actions/address/addressActions';
import {
  joinMyJohnLewis,
  setMyJLModalContentIndex,
  enterNewDetails,
  toggleFullScreenSignUp,
} from '@redux/actions/create-account/createAccountActions';
import { triggerAnalyticsEvent } from '@redux/actions/analytics/analyticsAction';
import {
  getMyJohnLewisAddressFormSimpleConfig,
  getMyJohnLewisAddressFormEditConfig,
  getMyJohnLewisAddressFormNewConfig,
  MY_JOHN_LEWIS_ADDRESS_FORM_SIMPLE_ID,
  MY_JOHN_LEWIS_ADDRESS_FORM_EDIT_ID,
  MY_JOHN_LEWIS_ADDRESS_FORM_NEW_ID,
} from '@utils/form/configs/myJohnLewis';
import myJohnLewisState from './MyJohnLewis.state';

// Components
import MyJohnLewisDetails from './MyJohnLewisDetails';
import BodyText from '@components/body-text';
import Container from '@components/container';
import Heading from '@components/heading';
import MyAccountLink from '@components/my-account-link';

// Styles
import styles from './my-john-lewis.scss';

export const formConfigMap = [
  MY_JOHN_LEWIS_ADDRESS_FORM_SIMPLE_ID,
  MY_JOHN_LEWIS_ADDRESS_FORM_EDIT_ID,
  MY_JOHN_LEWIS_ADDRESS_FORM_NEW_ID,
];

const MyJohnLewis = ({
  joinMyJLSuccess = false,
  sidebarView = false,
}: MyJohnLewisProps) => {
  const dispatch: AppDispatch = useDispatch();

  const {
    userAddress = {
      addressee: {},
      address: {},
      phoneNumber: '',
    },
    myJLContentIndex,
    myJLMemberStatus,
    loqateApiKey,
    sessionExpiredAnnounced,
    sessionId,
    isInternationalAddress,
    isApps,
    showMyJLModal,
    showFullScreenSignup,
    allowMyJlSignUps,
  } = useSelector(myJohnLewisState);

  const prevMyJLContentIndex = useRef(myJLContentIndex);
  const prevJoinMyJLSuccess = useRef(joinMyJLSuccess);

  useEffect(() => {
    if (prevMyJLContentIndex.current === 2 && myJLContentIndex === 1) {
      dispatch(resetAddressSearch({ addressId: MYJL_ADDRESS }));
      dispatch(destroyForm(formConfigMap[prevMyJLContentIndex.current]));
    }

    if (prevMyJLContentIndex.current === 1 && myJLContentIndex === 0) {
      dispatch(destroyForm(formConfigMap[prevMyJLContentIndex.current]));
    }

    if (!prevJoinMyJLSuccess.current && joinMyJLSuccess) {
      dispatch(push(routeConstants.ORDER_CONFIRMATION));
    }
  }, [myJLContentIndex, joinMyJLSuccess]);

  const onJoinMyJLClick = () => {
    if (sessionExpiredAnnounced) {
      dispatch(openSessionEndedModal());
    } else {
      dispatch(push(routeConstants.ORDER_CONFIRMATION_JOIN_MY_JL));

      dispatch(triggerAnalyticsEvent(ANALYTICS_MY_JL_JOIN_MODAL_OPEN, {
        international: isInternationalAddress,
      }));
    }
  };

  const onMyJLModalClose = () => {
    dispatch(push(routeConstants.ORDER_CONFIRMATION));
  };

  const onMyJlAddressFormSubmit = (formValues: any) => {
    return dispatch(joinMyJohnLewis(formValues));
  };

  const onModalBackClick = () => {
    dispatch(goBack());

    const newIndex = myJLContentIndex - 1;
    updateModalContent(newIndex);
  };

  const onMyJlEditAddress = () => {
    updateModalContent(1);
  };

  function updateModalContent(index: number) {
    dispatch(setMyJLModalContentIndex(index));
  }

  function renderSuccessMessage(testAttr = 'myjl-success-message') {
    return (
      <Container
        className={cx(styles.successMessage, {
          [styles.successMessageSidebar]: sidebarView,
        })}
        testId={testAttr}
      >
        {!showMyJLModal && <Heading label="my John Lewis" marginBottom="2" tag="h2" type="s" />}
        <Container
          background={!showMyJLModal ? 'white' : ''}
          paddingX={!showMyJLModal ? '4' : ''}
          paddingY={!showMyJLModal ? '2' : ''}
        >
          {myJLMemberStatus === userConstants.MY_JL_JOINED && (
            <>
              <Heading label="You’re in!" marginBottom="1" tag="h3" />
              <p>
                Head to <MyAccountLink isApps={isApps} /> to view your rewards and start enjoying the benefits of being
                a My John Lewis member.
              </p>

              {!isApps && (
                <div className={styles.downloadApp} data-testid="my-john-lewis-apps-links">
                  <Heading label="Download the app" marginBottom="1" tag="h4" type="xs" />
                  <p>
                    It’s the quickest way to see your rewards, access your card, and has even more exclusive benefits.
                  </p>

                  <div className={styles.qrCode}>
                    <p>Just scan this QR code from your device to download the app for iOS or Android now.</p>
                  </div>

                  <div className={styles.appStoreLinks}>
                    <a
                      href="https://play.google.com/store/apps/details?id=com.johnlewis.android&hl=en_GB&gl=US"
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      Get it on Google Play
                    </a>
                    <a
                      href="https://apps.apple.com/gb/app/john-lewis-partners/id486502369"
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      Download on the App Store
                    </a>
                  </div>
                </div>
              )}
            </>
          )}
          {myJLMemberStatus === userConstants.MY_JL_ACCEPTED && (
            <p>
              Please allow up to 24 hours for your account to be updated. Your membership card will be stored online and
              can be accessed when signing in to the John Lewis & Partners website or app. No need to carry a plastic
              card.
            </p>
          )}
        </Container>
      </Container>
    );
  }

  const renderEnterNewDetails = () => {
    return (
      <>
        Please update your details or alternatively{' '}
        <UnderlinedButton data-testid="myjl-enter-new-address" onClick={() => dispatch(enterNewDetails())}>
          enter new details
        </UnderlinedButton>{' '}
        to sign up for my John Lewis.
      </>
    );
  };

  function renderModalContent(ukOnlyMessage: string) {
    const { address: { countryCode } = {} } = userAddress;
    const isInternationalAddress = countryCode !== 'GB';

    if (joinMyJLSuccess) {
      return renderSuccessMessage('myjl-success-modal');
    }

    const commonProps = {
      onMyJlAddressFormSubmit: (formValues: any) => dispatch(onMyJlAddressFormSubmit(formValues)),
      triggerLoqateTimeoutEnterAddressManuallyAnalytics: () =>
        dispatch(triggerLoqateTimeoutEnterAddressManuallyAnalytics()),
      onLoqateNetworkTimeoutCallback: (url: string) => dispatch(onLoqateNetworkTimeoutCallback(url)),
      onLoqateSearch: (
        response: any,
        actionType: any,
        json: any,
      ) => dispatch(onLoqateSearch(response, actionType, json)),
      onEnterManualAddress: () => dispatch(onEnterManualAddress()),
      loqateApiKey,
      showFullScreenSignup,
    };

    const newAddressRequired = isInternationalAddress || myJLContentIndex > 1;

    if (newAddressRequired) {
      return (
        <MyJohnLewisDetails
          key={MY_JOHN_LEWIS_ADDRESS_FORM_NEW_ID}
          formId={MY_JOHN_LEWIS_ADDRESS_FORM_NEW_ID}
          getFormConfig={getMyJohnLewisAddressFormNewConfig}
          title={`Please enter your details to sign up for my John Lewis and enjoy our exclusive rewards.${ukOnlyMessage}`}
          {...commonProps}
        />
      );
    }

    const commonEditFormProps = {
      ...commonProps,
      disablePostcodeSearch: true,
      sessionId,
      userAddress,
    };

    const editFormConfig = [{
      ...commonEditFormProps,
      key: MY_JOHN_LEWIS_ADDRESS_FORM_SIMPLE_ID,
      formId: MY_JOHN_LEWIS_ADDRESS_FORM_SIMPLE_ID,
      getFormConfig: getMyJohnLewisAddressFormSimpleConfig,
      onMyJlEditAddress,
      simpleForm: true,
      title: 'Please confirm your details to sign up for my John Lewis and enjoy our exclusive rewards.',
    }, {
      ...commonEditFormProps,
      key: MY_JOHN_LEWIS_ADDRESS_FORM_EDIT_ID,
      formId: MY_JOHN_LEWIS_ADDRESS_FORM_EDIT_ID,
      getFormConfig: getMyJohnLewisAddressFormEditConfig,
      title: renderEnterNewDetails(),
    }];

    return <MyJohnLewisDetails {...editFormConfig[myJLContentIndex]} />;
  }

  if (!allowMyJlSignUps) {
    return null;
  }

  const showBackButton = myJLContentIndex > 0;
  const ukOnlyMessage = isInternationalAddress ? ' Membership is only available to UK residents.' : '';

  if (showFullScreenSignup) {
    return (
      <>
        <Container background="white" className={styles.logoContainer} marginBottom="2" testId="my-john-lewis-full-screen">
          <div className={styles.logo} />
        </Container>
        {renderModalContent(ukOnlyMessage)}
        <UnderlinedButton className={styles.backToOrder} onClick={() => dispatch(toggleFullScreenSignUp(false))}>
          Go back to your order
        </UnderlinedButton>
      </>
    );
  }

  return (
    <Container hideForPrint marginBottom="2" marginTop="1">
      {!joinMyJLSuccess && (
        <Container className={styles.prompt} padding="3" testId="myjl-prompt-message">
          <Heading className={styles.textHeading} label="Join My John Lewis" marginBottom="2" tag="h2" type="s" />
          <Heading className={styles.rewardingHeading} tag="h4">
            Rewarding little moments
          </Heading>
          <BodyText className={styles.promptContent} marginBottom="2">
            Get a £5 e-gift card when you spend your first £50, plus personalised offers, exclusive early access to
            new launches, members-only events and more
          </BodyText>
          <SecondaryButton className={styles.button} data-testid="myjl-join-link" onClick={onJoinMyJLClick}>
            Join now
          </SecondaryButton>
        </Container>
      )}

      {joinMyJLSuccess && renderSuccessMessage()}

      {showMyJLModal && (
        <Modal
          backButton={showBackButton}
          className={styles.modal}
          footer={false}
          height="hug"
          isOpen
          onBackButtonClick={onModalBackClick}
          onClose={onMyJLModalClose}
          title="Add My John Lewis details"
        >
          {renderModalContent(ukOnlyMessage)}
        </Modal>
      )}
    </Container>
  );
};

export default MyJohnLewis;
